import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClockIcon, LockIcon, ShieldCheckIcon, TriangleAlertIcon } from 'lucide-react';
import { BrandMark } from '../../components/BrandMark';
import { Button } from '../../components/ui/Button';
import { useLocale } from '../../contexts/LocaleContext';
type Stage = 'credentials' | 'twofactor';
function Frame({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    t
  } = useLocale();
  return <div className="flex min-h-screen w-full bg-deep" data-id="element-1704" data-mp="5dk8aui4gx1l">
      <section className="hidden min-w-0 flex-1 flex-col justify-between border-r border-sand/12 p-10 lg:flex" data-id="element-1705" data-mp="5f522hwm4bye">
        <div className="flex items-center gap-2" data-id="element-1706" data-mp="jemxhg4eyngq">
          <BrandMark size={34} data-id="element-1707" data-mp="edqsez8bsv9q" />
          <div data-id="element-1708" data-mp="ecp4vva4igb4">
            <p className="bee-overline text-[9px] font-bold text-gold" data-id="element-1709" data-mp="ecxlny9da5rl">
              {t('adm.brand')}
            </p>
            <p className="text-[16px] font-bold text-sand" data-id="element-1710" data-mp="1uubg5vamx0bq">BEELYV</p>
          </div>
        </div>
        <div className="max-w-[46ch]" data-id="element-1711" data-mp="1pvrtfso7vkib">
          <h1 className="text-[30px] font-bold leading-tight text-sand" data-id="element-1712" data-mp="1won82zq1mmzq">
            {t('adm.login.headline')}
          </h1>
          <p className="mt-3 text-[13.5px] leading-relaxed text-ondark" data-id="element-1713" data-mp="20nw3sa4n8uxx">
            {t('adm.login.body')}
          </p>
        </div>
        <ul className="space-y-1" data-id="element-1714" data-mp="25mqrnqhgx9ow">
          {['adm.login.p1', 'adm.login.p2', 'adm.login.p3'].map(key => <li key={key} className="flex items-start gap-2 text-[12px] text-ondark" data-id="element-1715" data-mp="1d063eywahdpk">
              <span className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-pill bg-gold" aria-hidden="true" data-id="element-1716" data-mp="1hyz6ryhv3oc0" />
              {t(key)}
            </li>)}
        </ul>
      </section>

      <section className="flex w-full items-center justify-center p-6 lg:w-[520px] lg:shrink-0" data-id="element-1717" data-mp="1rp28qo6yocmh">
        <div className="w-full max-w-[380px]" data-id="element-1718" data-mp="ua6i8n3kkirf">{children}</div>
      </section>
    </div>;
}
export function AdminLogin() {
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const expired = params.get('estado') === 'expirada';
  const [stage, setStage] = useState<Stage>('credentials');
  const [email, setEmail] = useState('marina@beelyv.com');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [code, setCode] = useState('');
  const submit = () => {
    if (password.trim().length < 6) {
      setError(true);
      return;
    }
    setError(false);
    setStage('twofactor');
  };
  if (stage === 'twofactor') {
    return <Frame data-id="element-1719" data-mp="48nn62maiaiq">
        <div className="rounded-panel border border-sand/12 bg-deeper p-5" data-id="element-1720" data-mp="495hgtpe6eo0">
          <span className="grid h-12 w-12 place-items-center rounded-pill border border-gold/45 bg-deep text-gold" data-id="element-1721" data-mp="i7wvkg4980c5">
            <ShieldCheckIcon size={20} data-id="element-1722" data-mp="d6plubijwr2u" />
          </span>
          <h2 className="mt-3 text-[20px] font-bold text-sand" data-id="element-1723" data-mp="1tlr48c0qs5hu">{t('adm.2fa.title')}</h2>
          <p className="mt-1 text-[12.5px] leading-relaxed text-ondark" data-id="element-1724" data-mp="1onfus2eczx05">
            {t('adm.2fa.body')}
          </p>

          <label className="mt-4 block" data-id="element-1725" data-mp="36kj7uvysm6nm">
            <span className="bee-overline text-[8.5px] font-bold text-ondark" data-id="element-1726" data-mp="36mx3p71armw8">
              {t('adm.2fa.code')}
            </span>
            <input value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="000000" aria-label={t('adm.2fa.code')} className="bee-num mt-1 h-12 w-full rounded-card border border-sand/20 bg-deep px-3 text-[20px] font-bold tracking-[0.3em] text-sand placeholder:text-ondark focus:border-gold/60 focus:outline-none" data-id="element-1727" data-mp="2xke4cwwpzseq" />
          </label>

          <Button variant="gold" size="lg" fullWidth className="mt-4" disabled={code.length < 6} onClick={() => navigate('/admin')} data-id="element-1728" data-mp="3lhtm2d2tlqn1">
            {t('adm.2fa.confirm')}
          </Button>
          <button type="button" onClick={() => setStage('credentials')} className="mt-3 w-full text-[12px] font-medium text-ondark transition-colors duration-150 ease-brand hover:text-sand" data-id="element-1729" data-mp="hv30a9p05unr">
            {t('adm.action.back')}
          </button>
        </div>
      </Frame>;
  }
  return <Frame data-id="element-1730" data-mp="2bdbmbau9f7mx">
      <div className="rounded-panel border border-sand/12 bg-deeper p-5" data-id="element-1731" data-mp="2bdjxmyaqi4w0">
        <span className="grid h-12 w-12 place-items-center rounded-pill border border-sand/20 bg-deep text-gold" data-id="element-1732" data-mp="382t0yfb8040r">
          <LockIcon size={20} data-id="element-1733" data-mp="3417h2a09qph2" />
        </span>
        <h2 className="mt-3 text-[20px] font-bold text-sand" data-id="element-1734" data-mp="34292n8ewuud1">{t('adm.login.title')}</h2>
        <p className="mt-1 text-[12.5px] leading-relaxed text-ondark" data-id="element-1735" data-mp="2z969v2hyjoxw">
          {t('adm.login.subtitle')}
        </p>

        {expired && <p className="mt-3 flex items-start gap-2 rounded-card border border-gold/40 bg-gold/10 p-2 text-[12px] leading-snug text-gold" data-id="element-1736" data-mp="3rqs2czjw9yti">
            <ClockIcon size={14} className="mt-[1px] shrink-0" aria-hidden="true" data-id="element-1737" data-mp="3mzjs61aqq9fa" />
            {t('adm.login.expired')}
          </p>}

        <label className="mt-4 block" data-id="element-1738" data-mp="3udsyjkxrkee1">
          <span className="bee-overline text-[8.5px] font-bold text-ondark" data-id="element-1739" data-mp="34dma7gvfhmj">
            {t('adm.login.email')}
          </span>
          <input type="email" value={email} onChange={event => setEmail(event.target.value)} className="mt-1 h-11 w-full rounded-card border border-sand/20 bg-deep px-2 text-[13.5px] text-sand focus:border-gold/60 focus:outline-none" data-id="element-1740" data-mp="82ow4i761s4r" />
        </label>

        <label className="mt-3 block" data-id="element-1741" data-mp="m1y9la3zm851">
          <span className="bee-overline text-[8.5px] font-bold text-ondark" data-id="element-1742" data-mp="qu7vmf1zeo21">
            {t('adm.login.password')}
          </span>
          <input type="password" value={password} onChange={event => {
          setPassword(event.target.value);
          setError(false);
        }} placeholder="••••••••" aria-invalid={error} aria-describedby={error ? 'admin-login-error' : undefined} className={`mt-1 h-11 w-full rounded-card border bg-deep px-2 text-[13.5px] text-sand placeholder:text-ondark focus:outline-none ${error ? 'border-gold' : 'border-sand/20 focus:border-gold/60'}`} data-id="element-1743" data-mp="vsj6mt40j4lu" />
        </label>

        {error && <p id="admin-login-error" role="alert" className="mt-2 flex items-start gap-2 text-[11.5px] leading-snug text-gold" data-id="element-1744" data-mp="35unif47lrdm8">
            <TriangleAlertIcon size={13} className="mt-[1px] shrink-0" aria-hidden="true" data-id="element-1745" data-mp="3fls0wdwhlglt" />
            {t('adm.login.invalid')}
          </p>}

        <Button variant="gold" size="lg" fullWidth className="mt-4" onClick={submit} data-id="element-1746" data-mp="3onho17jc5tds">
          {t('adm.login.continue')}
        </Button>

        <button type="button" className="mt-3 w-full text-[12px] font-medium text-ondark transition-colors duration-150 ease-brand hover:text-sand" data-id="element-1747" data-mp="1z9336ybfuqnk">
          {t('adm.login.forgot')}
        </button>
      </div>
    </Frame>;
}